import { Injectable } from '@angular/core';
import { ComponentEffects, logCatchError } from '../common';
import { PrintModalComponent } from '../components/modals/print-modal';

import { ofType } from '@ngrx/effects';
import { catchError, exhaustMap, filter, from, map, switchMap, tap, throwError, withLatestFrom }
  from 'rxjs';
import { GoogleAnalyticsEvent, GoogleAnalyticsService } from '@mhe/reader/features/analytics';
import { MatDialog } from '@angular/material/dialog';
import { ReaderConfigStore, ReaderStore } from '../components/reader/state';
import * as printActions from '../components/reader/state/print.actions';
import { TocStore } from '../components/toc';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationModalComponent, ConfirmationModalData }
  from '../components/modals/confirmation-modal';
import { PrintService } from '@mhe/reader/core/reader-api';
import { MediatorUtils } from './mediator-utils';
import { PdfRequest } from '@mhe/reader/models';

@Injectable()
export class PrintMediator extends ComponentEffects {
  private readonly readerActions$ = this.readerStore.actions$;
  private readonly printWorker = new Worker('/assets/workers/print.worker.js');

  constructor(
    private readonly readerStore: ReaderStore,
    private readonly readerConfigStore: ReaderConfigStore,
    private readonly tocStore: TocStore,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly printService: PrintService,
    private readonly utils: MediatorUtils,
    private readonly ga: GoogleAnalyticsService,
  ) {
    super();
  }

  readonly printButtonClicked$ = this.effect(() =>
    this.readerActions$.pipe(
      ofType(printActions.printButtonClicked),

      // Open chapter-selection modal and wait for selection
      withLatestFrom(this.tocStore.rootNodes$),
      exhaustMap(([_, nodes]) =>
        this.dialog
          .open(PrintModalComponent, {
            data: { chapters: nodes.map(n => ({ id: n.tocItem?.id, name: n.title })) },
            height: '80%',
            width: '80%',
            ariaLabelledBy: 'print-modal-h2',
          })
          .afterClosed(),
      ),
      filter(chapter => !!chapter),

      // Confirm with user that PDF generation should proceed
      exhaustMap(chapter => {
        const confirmData: ConfirmationModalData = {
          closeText: this.translate.instant('shared.cancel'),
          confirmText: this.translate.instant('shared.ok'),
          content: this.translate.instant('dialog.generating_pdf_message'),
          title: this.translate.instant('dialog.title.generating_pdf'),
        };
        return this.dialog
          .open(ConfirmationModalComponent, { data: confirmData, ariaLabelledBy: 'confirmation-modal-h2' })
          .afterClosed()
          .pipe(map(ok => ({ ok, chapter })));
      }),
      filter(({ ok }) => ok),

      withLatestFrom(
        this.utils.readerApi$,
        this.utils.requestContext$,
        this.readerConfigStore.fallbackToDefaultPublish$,
      ),

      // Build request payload and call PrintService
      switchMap(([{ chapter }, apiUrl, requestContext, fallback]) => {
        const { contextID, platform, userID, epubReleaseUUID, epubVersion } = requestContext;

        let pdfRequest: Partial<PdfRequest> = {
          contextID,
          platform,
          userID,
          chapterId: chapter.id,
          chapterName: chapter.name,
          printedAt: new Date().toISOString(),
        };
        if (!fallback) {
          pdfRequest = { ...pdfRequest, epubReleaseUUID, epubVersion };
        }

        // Set print icon to loading spinner
        this.readerStore.setPdfLoading(true);

        // GA event template
        const gaEvent: GoogleAnalyticsEvent = {
          eventCategory: 'Print ePub',
          eventAction: 'Generate PDF',
          eventLabel:
            `User: ${userID}, ePub: ${contextID}, Platform: ${platform}, ` +
            `ReleaseUUID: ${epubReleaseUUID}, Version: ${epubVersion}, ` +
            `chapterName: ${chapter.name}, chapterId: ${chapter.id}`,
        };

        return this.printService.getPdf(apiUrl, pdfRequest).pipe(
          switchMap(pdfResponse => {
            const { preSignedURL, encryptedUserID = '' } = pdfResponse;
            const pdfMetaData = { title: chapter.name, subject: contextID };
            const workerPromise = new Promise<string>((resolve, reject) => {
              this.printWorker.onmessage = ({ data }) =>
                data.success ? resolve(data.downloadUrl) : reject(new Error(data.error));
              this.printWorker.onerror = errEvt => reject(errEvt);

              this.printWorker.postMessage({
                presignedUrl: preSignedURL,
                encryptedUserID,
                ...pdfMetaData,
              });
            });
            return from(workerPromise).pipe(map(downloadUrl => ({ downloadUrl })));
          }),

          // On success, fire GA success, clear spinner, and store blob URL
          tap(({ downloadUrl }) => {
            this.ga.event({ ...gaEvent, eventValue: 1 });
            this.readerStore.setPdfLoading(false);
            this.readerStore.setPdfUrl({ url: downloadUrl, title: chapter.name });
          }),

          // On error, fire GA failure, clear spinner, show alert
          catchError(err => {
            this.ga.event({ ...gaEvent, eventValue: 0 });
            this.readerStore.setPdfLoading(false);
            this.readerStore.addAlert({ alertType: 'alert-warning', translateKey: 'print.error' });
            return throwError(() => err);
          }),
        );
      }),

      // Global catch
      logCatchError('printButtonClicked$'),
    ),
  );

  private readonly handlePrintAlertClose$ = this.effect(() =>
    this.readerActions$.pipe(
      ofType(printActions.showCloseAlertModal),
      exhaustMap(() => {
        const confirmData: ConfirmationModalData = {
          closeText: this.translate.instant('shared.cancel'),
          confirmText: this.translate.instant('shared.ok'),
          content: this.translate.instant(
            'dialog.are_you_sure.dismiss_print_file',
          ),
          title: this.translate.instant('dialog.title.print'),
        };

        const confirmDialog$ = this.dialog
          .open(ConfirmationModalComponent, {
            data: confirmData,
            ariaLabelledBy: 'confirmation-modal-h2',
          })
          .afterClosed();
        return confirmDialog$.pipe(
          filter((ok) => ok),
          tap(() => {
            this.readerStore.setPdfUrl(undefined);
          }),
        );
      }),
      logCatchError('handlePrintAlertClose$'),
    ),
  );
}
